import type { ArchiveType, ControlDto, CreateControlInput, CreateKpiInput, KpiDto, Role } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, Field, PageTitle, Select } from '../../ui';
import { formatValue, unitOf } from './KpiChart';

const human = (k: string) => k.replace(/_/g, ' ');

function NewControl({ types, roles, onDone }: { types: ArchiveType[]; roles: Role[]; onDone: () => void }) {
  const { t } = useTenant();
  const [name, setName] = useState('');
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [filter, setFilter] = useState({ attributo: '', valore: '' });
  const [keyA, setKeyA] = useState('');
  const [keyB, setKeyB] = useState('');
  const [mode, setMode] = useState<'uguale' | 'contiene'>('uguale');
  const [fields, setFields] = useState<string[]>([]);
  const [role, setRole] = useState('');
  const ta = types.find((x) => x.nome === a);
  const tb = types.find((x) => x.nome === b);
  const create = useMutation({
    mutationFn: () =>
      api<ControlDto>(t('/controls'), {
        body: {
          name,
          tipo_a: a,
          filtri_a: filter.attributo && filter.valore ? [{ attributo: filter.attributo, operatore: 'uguale', valore: filter.valore }] : [],
          tipo_b: b,
          chiavi: [{ a: keyA, b: keyB, modo: mode }],
          campi: fields.length ? fields : [keyA],
          ruolo: role,
        } satisfies Partial<CreateControlInput>,
      }),
    onSuccess: onDone,
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Field label="Nome del controllo" placeholder="es. Milestone chiuse non fatturate" value={name} onChange={(e) => setName(e.target.value)} required />
      <Select label="Ogni elemento di…" value={a} onChange={(e) => setA(e.target.value)} required>
        <option value="">scegli dall’archivio</option>
        {types.map((x) => <option key={x.nome} value={x.nome}>{x.etichetta} ({x.entita})</option>)}
      </Select>
      {ta && (
        <div className="grid grid-cols-2 gap-2">
          <Select label="…solo se (facoltativo)" value={filter.attributo} onChange={(e) => setFilter({ ...filter, attributo: e.target.value })}>
            <option value="">tutti</option>
            {ta.attributi.map((x) => <option key={x.nome} value={x.nome}>{human(x.nome)}</option>)}
          </Select>
          <Field label="è uguale a" value={filter.valore} onChange={(e) => setFilter({ ...filter, valore: e.target.value })} />
        </div>
      )}
      <Select label="…deve avere un corrispondente in" value={b} onChange={(e) => setB(e.target.value)} required>
        <option value="">scegli dall’archivio</option>
        {types.filter((x) => x.nome !== a).map((x) => <option key={x.nome} value={x.nome}>{x.etichetta} ({x.entita})</option>)}
      </Select>
      {ta && tb && (
        <div className="grid grid-cols-3 gap-2">
          <Select label={`campo di ${ta.etichetta}`} value={keyA} onChange={(e) => setKeyA(e.target.value)} required>
            <option value="" />
            {ta.attributi.map((x) => <option key={x.nome} value={x.nome}>{human(x.nome)}</option>)}
          </Select>
          <Select label="confronto" value={mode} onChange={(e) => setMode(e.target.value as 'uguale' | 'contiene')}>
            <option value="uguale">uguale a</option>
            <option value="contiene">contenuto in</option>
          </Select>
          <Select label={`campo di ${tb.etichetta}`} value={keyB} onChange={(e) => setKeyB(e.target.value)} required>
            <option value="" />
            {tb.attributi.map((x) => <option key={x.nome} value={x.nome}>{human(x.nome)}</option>)}
          </Select>
        </div>
      )}
      {ta && (
        <div>
          <p className="mb-1 text-xs font-medium text-ink/70">Cosa mostrare a chi verifica</p>
          <div className="flex flex-wrap gap-1">
            {ta.attributi.map((x) => (
              <button
                type="button"
                key={x.nome}
                className={`rounded-full border px-2 py-0.5 text-xs ${fields.includes(x.nome) ? 'border-accent bg-accent/10' : 'border-black/15'}`}
                onClick={() => setFields(fields.includes(x.nome) ? fields.filter((f) => f !== x.nome) : [...fields, x.nome].slice(0, 10))}
              >
                {human(x.nome)}
              </button>
            ))}
          </div>
        </div>
      )}
      <Select label="Chi verifica le eccezioni" value={role} onChange={(e) => setRole(e.target.value)} required>
        <option value="" />
        {roles.map((r) => <option key={r.key} value={r.key}>{human(r.key)}</option>)}
      </Select>
      <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Creazione…' : 'Crea il controllo'}</Button>
      <ErrorText>{create.error ? errorMessage(create.error) : ''}</ErrorText>
    </form>
  );
}

function ControlCard({ c }: { c: ControlDto }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'controls'] });
  const run = useMutation({ mutationFn: () => api(t(`/controls/${c.id}/run`), { method: 'POST' }), onSuccess: refresh });
  const toggle = useMutation({ mutationFn: () => api(t(`/controls/${c.id}/enabled`), { body: { enabled: !c.enabled } }), onSuccess: refresh });
  const verified = Object.values(c.outcomes).reduce((s, n) => s + n, 0);
  // L'ultimo esito della lista vale "falso allarme".
  const falseAlarms = c.outcomes[c.config.esiti[c.config.esiti.length - 1] ?? 'non_e_un_problema'] ?? 0;
  const r = c.last_result;
  return (
    <Card>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{c.name}</p>
          <p className="text-xs text-ink/55">
            Verifica che {c.spiegazione} · chi verifica: {human(c.config.ruolo)}
          </p>
          {c.description && <p className="mt-1 text-xs text-ink/55">{c.description}</p>}
        </div>
        {c.open_exceptions > 0 ? <Badge tone="amber">{c.open_exceptions} da verificare</Badge> : <Badge tone="green">nessuna eccezione aperta</Badge>}
        {!c.enabled && <Badge>sospeso</Badge>}
      </div>
      <p className="mt-2 text-sm text-ink/70">
        {r ? `Ultimo confronto ${dateTime(c.last_run_at)}: ${r.totale} controllati, ${r.abbinate} a posto, ${r.nuove} nuove eccezioni, ${r.risolte} risolte da sole${r.non_verificabili ? `, ${r.non_verificabili} non verificabili per dati mancanti` : ''}.` : 'Non ancora eseguito: gira ogni notte, oppure subito.'}
      </p>
      {verified > 0 && (
        <p className="mt-1 text-xs text-ink/55">
          Verifiche fatte: {verified} · falsi allarmi {Math.round((falseAlarms / verified) * 100)}%
          {' ('}
          {Object.entries(c.outcomes).map(([k, n]) => `${human(k)} ${n}`).join(', ')})
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {isManager && (
          <Button variant="ghost" disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Confronto…' : 'Esegui ora'}
          </Button>
        )}
        <Button variant="ghost" onClick={() => navigate(`/t/${tenant.id}/processi/${c.process_id}`)}>Processo di verifica</Button>
        {isManager && <Button variant="ghost" onClick={() => toggle.mutate()}>{c.enabled ? 'Sospendi' : 'Riattiva'}</Button>}
      </div>
      <ErrorText>{run.error ? errorMessage(run.error) : ''}</ErrorText>
    </Card>
  );
}

/** Controlli permanenti: un'attesa tra due insiemi di dati; ciò che non torna diventa un compito di verifica. */
export function ControlsPage() {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const list = useTenantQuery<{ data: ControlDto[] }>(['controls'], '/controls');
  const types = useTenantQuery<{ data: ArchiveType[] }>(['archive', 'types'], '/archive/types');
  const roles = useTenantQuery<{ data: Role[] }>(['roles'], '/roles');
  const [creating, setCreating] = useState(false);
  // Il sistema guarda i dati e propone i controlli di gestione utili: diventano proposte da decidere.
  const design = useMutation({
    mutationFn: () => api<{ proposte: number; scartate: number; saltato: string | null }>(t('/controls/design'), { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'proposals'] }),
  });
  return (
    <>
      <PageTitle
        title="Controlli"
        subtitle="Cose che devono sempre tornare tra i vostri dati. Quando non tornano, qualcuno verifica: e il sistema impara quanto il controllo è utile."
        actions={
          isManager && !creating ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" disabled={design.isPending} onClick={() => design.mutate()}>
                {design.isPending ? 'Guardo i dati…' : 'Proponi controlli'}
              </Button>
              <Button onClick={() => setCreating(true)}>Nuovo controllo</Button>
            </div>
          ) : undefined
        }
      />
      <ErrorText>{list.error ? errorMessage(list.error) : design.error ? errorMessage(design.error) : ''}</ErrorText>
      {design.data && (
        <Card className="mb-4">
          <p className="text-sm">
            {design.data.proposte
              ? `${design.data.proposte} ${design.data.proposte === 1 ? 'controllo proposto' : 'controlli proposti'}, già provati sui vostri dati.`
              : design.data.saltato === 'proposte_aperte'
                ? 'Ci sono già proposte di controllo da decidere: prima quelle.'
                : design.data.saltato === 'nessun_dato'
                  ? 'Nell’archivio non ci sono ancora dati su cui fare controlli.'
                  : 'Nessun controllo nuovo da proporre con i dati di oggi.'}{' '}
            {design.data.proposte > 0 && (
              <button className="underline" onClick={() => navigate(`/t/${tenant.id}/proposte`)}>
                Vai alle proposte
              </button>
            )}
          </p>
        </Card>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0 space-y-4">
          {list.data?.data.length === 0 && <Empty>Nessun controllo. Servono almeno due tipi di dati nell’archivio (da export caricati o da programmi collegati).</Empty>}
          {list.data?.data.map((c) => <ControlCard key={c.id} c={c} />)}
        </div>
        {creating && (
          <Card className="h-fit">
            <div className="mb-3 flex items-center">
              <h2 className="flex-1 text-sm font-semibold">Nuovo controllo</h2>
              <button className="text-xs text-ink/50" onClick={() => setCreating(false)}>chiudi</button>
            </div>
            {(types.data?.data.length ?? 0) < 2 ? (
              <p className="text-sm text-ink/60">Nell’archivio servono almeno due tipi di dati da confrontare.</p>
            ) : (
              <NewControl
                types={types.data!.data}
                roles={roles.data?.data ?? []}
                onDone={() => {
                  setCreating(false);
                  void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'controls'] });
                }}
              />
            )}
          </Card>
        )}
      </div>
    </>
  );
}

type Native = { process_id: string; name: string; runs: number; closed: number; avg_minutes: number | null; rework: number | null };

function NewKpi({ types, onDone }: { types: ArchiveType[]; onDone: () => void }) {
  const { t } = useTenant();
  const [name, setName] = useState('');
  const [tipo, setTipo] = useState('');
  const [metrica, setMetrica] = useState<CreateKpiInput['metrica']>('conteggio');
  const [attributo, setAttributo] = useState('');
  const [data, setData] = useState('');
  const [grana, setGrana] = useState<NonNullable<CreateKpiInput['grana']>>('mese');
  const [filter, setFilter] = useState({ attributo: '', valore: '' });
  const ty = types.find((x) => x.nome === tipo);
  const numeric = ty?.attributi.filter((x) => x.tipo === 'numero') ?? [];
  const dates = ty?.attributi.filter((x) => x.tipo === 'data') ?? [];
  const create = useMutation({
    mutationFn: () =>
      api<KpiDto>(t('/kpis'), {
        body: {
          name,
          tipo,
          metrica,
          attributo: metrica === 'conteggio' ? null : attributo,
          filtri: filter.attributo && filter.valore ? [{ attributo: filter.attributo, operatore: 'uguale', valore: filter.valore }] : [],
          attributo_data: data || null,
          grana: data ? grana : null,
        },
      }),
    onSuccess: onDone,
  });
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <Field label="Nome" placeholder="es. Ore fatturate al mese" value={name} onChange={(e) => setName(e.target.value)} required />
      <Select label="Dati" value={tipo} onChange={(e) => setTipo(e.target.value)} required>
        <option value="">scegli dall’archivio</option>
        {types.map((x) => <option key={x.nome} value={x.nome}>{x.etichetta}</option>)}
      </Select>
      {ty && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Select label="Misura" value={metrica} onChange={(e) => setMetrica(e.target.value as CreateKpiInput['metrica'])}>
              <option value="conteggio">quanti sono</option>
              <option value="somma" disabled={!numeric.length}>somma di</option>
              <option value="media" disabled={!numeric.length}>media di</option>
            </Select>
            {metrica !== 'conteggio' && (
              <Select label="campo" value={attributo} onChange={(e) => setAttributo(e.target.value)} required>
                <option value="" />
                {numeric.map((x) => <option key={x.nome} value={x.nome}>{human(x.nome)}</option>)}
              </Select>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select label="Solo se (facoltativo)" value={filter.attributo} onChange={(e) => setFilter({ ...filter, attributo: e.target.value })}>
              <option value="">tutti</option>
              {ty.attributi.map((x) => <option key={x.nome} value={x.nome}>{human(x.nome)}</option>)}
            </Select>
            <Field label="è uguale a" value={filter.valore} onChange={(e) => setFilter({ ...filter, valore: e.target.value })} />
          </div>
          {dates.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              <Select label="Nel tempo, per data di" value={data} onChange={(e) => setData(e.target.value)}>
                <option value="">un solo totale</option>
                {dates.map((x) => <option key={x.nome} value={x.nome}>{human(x.nome)}</option>)}
              </Select>
              {data && (
                <Select label="raggruppato per" value={grana} onChange={(e) => setGrana(e.target.value as NonNullable<CreateKpiInput['grana']>)}>
                  <option value="mese">mese</option>
                  <option value="giorno">giorno</option>
                  <option value="anno">anno</option>
                </Select>
              )}
            </div>
          )}
        </>
      )}
      <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Calcolo…' : 'Crea indicatore'}</Button>
      <ErrorText>{create.error ? errorMessage(create.error) : ''}</ErrorText>
    </form>
  );
}

function KpiCard({ k }: { k: KpiDto }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const remove = useMutation({ mutationFn: () => api(t(`/kpis/${k.id}/remove`), { method: 'POST' }), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'kpis'] }) });
  const max = Math.max(1, ...k.serie.map((s) => s.valore ?? 0));
  const fmt = (v: number | null | undefined) => formatValue(v, unitOf(k));
  const cov = k.copertura;
  const pct = cov.righe_totali ? Math.round(((cov.righe_totali - cov.righe_escluse) / cov.righe_totali) * 100) : 100;
  return (
    <Card>
      <div className="flex items-start gap-2">
        <p className="flex-1 font-semibold">{k.name}</p>
        <Badge tone={pct >= 95 ? 'green' : pct >= 70 ? 'amber' : 'red'}>copertura {pct}%</Badge>
      </div>
      {k.libreria && <p className="mt-0.5 text-xs text-ink/55">{k.libreria.a_cosa_serve}</p>}
      {k.chiuso ? (
        // Chi vede i dati del suo ruolo (ADR 0232): l'indicatore usa un dato che i suoi ruoli non leggono.
        <p className="mt-2 text-sm text-ink/60">Chiuso: {k.chiuso}.</p>
      ) : k.errore ? (
        <p className="mt-2 text-sm text-red-700">{k.errore}</p>
      ) : k.manca?.length ? (
        // Senza i dati non si mostra uno zero (ADR 0213 § 2): si dice cosa serve.
        <p className="mt-2 text-sm text-ink/60">
          Per vederlo servono: {k.manca.map((m) => (m.motivo === 'campi_vuoti' ? `${m.nome} con ${m.campi.map((c) => c.replace(/_/g, ' ')).join(' e ')}` : m.nome)).join('; ')}.
        </p>
      ) : (
        <div className="mt-3 space-y-1">
          {/* Con i gruppi (ADR 0213) ogni riga dice anche il gruppo; un valore che non si calcola resta «—». */}
          {k.serie.slice(k.config.raggruppa ? -36 : -12).map((s) => (
            <div key={`${s.periodo}/${s.gruppo ?? ''}`} className="flex items-center gap-2 text-sm">
              <span className="w-24 shrink-0 truncate text-xs text-ink/55" title={s.etichetta}>
                {s.periodo === 'totale' && s.etichetta ? s.etichetta : s.periodo}
                {s.periodo !== 'totale' && s.etichetta ? <span className="block truncate">{s.etichetta}</span> : null}
              </span>
              <div className="h-3 flex-1 rounded bg-black/5">
                <div className="h-3 rounded bg-accent/70" style={{ width: `${((s.valore ?? 0) / max) * 100}%` }} />
              </div>
              <span className="w-24 shrink-0 text-right tabular-nums">
                {fmt(s.valore)}
                {s.anno_prima !== undefined && <span className="block text-[11px] text-ink/45">anno prima {fmt(s.anno_prima)}</span>}
              </span>
            </div>
          ))}
          {k.serie.length === 0 && <p className="text-sm text-ink/55">Nessun dato.</p>}
        </div>
      )}
      <p className="mt-3 text-xs text-ink/60">{k.spiegazione}</p>
      {cov.fonti.length > 0 && (
        <p className="mt-1 text-xs text-ink/45">
          Fonti: {cov.fonti.map((f) => `${f.fonte} (${f.righe} righe, agg. ${dateTime(f.ultimo_aggiornamento)})`).join(' · ')}
        </p>
      )}
      {/* Quelli dell'azienda tipo seguono il gestionale: si tolgono con la tappa 4 dell'ADR 0213. */}
      {isManager && !k.libreria && (
        <button className="mt-2 text-xs text-ink/45 hover:text-red-700" onClick={() => confirm(`Eliminare l’indicatore “${k.name}”?`) && remove.mutate()}>
          elimina
        </button>
      )}
    </Card>
  );
}

/** Indicatori: numeri calcolati dall'archivio, sempre con la loro copertura (quanti dati ci sono dietro e da dove). */
export function KpisPage() {
  const { tenant, isManager } = useTenant();
  const queryClient = useQueryClient();
  const list = useTenantQuery<{ data: KpiDto[]; native: Native[] }>(['kpis'], '/kpis');
  const types = useTenantQuery<{ data: ArchiveType[] }>(['archive', 'types'], '/archive/types');
  const [creating, setCreating] = useState(false);
  const native = list.data?.native.filter((n) => n.runs > 0) ?? [];
  return (
    <>
      <PageTitle
        title="Indicatori"
        subtitle="Numeri sul lavoro, ognuno con la copertura: quanti dati lo sostengono e da dove arrivano."
        actions={isManager && !creating ? <Button onClick={() => setCreating(true)}>Nuovo indicatore</Button> : undefined}
      />
      <ErrorText>{list.error ? errorMessage(list.error) : ''}</ErrorText>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-4">
          <Card>
            <h2 className="mb-2 text-sm font-semibold">Dai processi (ultimi 30 giorni)</h2>
            {native.length === 0 ? (
              <p className="text-sm text-ink/55">Ancora nessuna esecuzione.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-xs text-ink/55">
                    <tr><th className="py-1 pr-3 font-medium">Processo</th><th className="pr-3 font-medium">Esecuzioni</th><th className="pr-3 font-medium">Chiuse</th><th className="pr-3 font-medium">Tempo medio</th><th className="font-medium">Rilavorazioni</th></tr>
                  </thead>
                  <tbody>
                    {native.map((n) => (
                      <tr key={n.process_id} className="border-t border-black/5">
                        <td className="py-1 pr-3">{n.name}</td>
                        <td className="pr-3 tabular-nums">{n.runs}</td>
                        <td className="pr-3 tabular-nums">{n.closed}</td>
                        <td className="pr-3 tabular-nums">{n.avg_minutes === null ? '—' : n.avg_minutes < 120 ? `${n.avg_minutes} min` : `${Math.round(n.avg_minutes / 60)} h`}</td>
                        <td className="tabular-nums">{n.rework === null ? '—' : `${n.rework}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          {list.data?.data.length === 0 && <Empty>Nessun indicatore sull’archivio. Creane uno dai dati caricati o dai programmi collegati.</Empty>}
          {list.data?.data.map((k) => <KpiCard key={k.id} k={k} />)}
        </div>
        {creating && (
          <Card className="h-fit">
            <div className="mb-3 flex items-center">
              <h2 className="flex-1 text-sm font-semibold">Nuovo indicatore</h2>
              <button className="text-xs text-ink/50" onClick={() => setCreating(false)}>chiudi</button>
            </div>
            <NewKpi
              types={types.data?.data ?? []}
              onDone={() => {
                setCreating(false);
                void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'kpis'] });
              }}
            />
          </Card>
        )}
      </div>
    </>
  );
}
